import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Check, Trash2, Eye, X } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, inputCls, cedis } from "./ui";

// ── Shared review grid: see images, preview, delete unwanted, then publish ──
export function ImportReview({ imported, categories, storeLabel, fx, onRemove, onPublish, publishing }) {
  const [edits, setEdits] = useState({});
  const [preview, setPreview] = useState(null);
  const catNames = (categories || []).map((c) => c.name);
  const subsFor = (name) => ((categories || []).find((c) => c.name === name)?.subcategories || []);
  const setEdit = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...e[id], [k]: v } }));

  const publish = () => {
    const bodies = imported.map((r) => {
      const p = r.product; const e = edits[p.id] || {};
      return {
        ...p,
        category: e.category ?? p.category,
        subcategory: e.subcategory ?? p.subcategory,
        supplier_category: p.supplier_category || "",
        tags: (e.tags != null ? e.tags : (p.tags || []).join(", ")).split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
        status: "published",
      };
    });
    onPublish(bodies);
  };

  if (!imported.length) return <p className="text-sm text-[#5C5C5C]">Nothing to review — all items were removed.</p>;

  return (
    <div className="space-y-3" data-testid="import-review">
      <p className="text-sm text-[#5C5C5C]">Review the {imported.length} imported item{imported.length > 1 ? "s" : ""}{storeLabel ? ` from ${storeLabel}` : ""}. Check the image, remove any you don't want, then publish. Nothing goes live until you publish.</p>
      <div className="space-y-3 max-h-[52vh] overflow-y-auto pr-1">
        {imported.map((r) => {
          const p = r.product; const e = edits[p.id] || {};
          const cat = e.category ?? p.category;
          const price = p.uk_price_gbp ?? p.price_gbp;
          return (
            <div key={p.id} className="bg-[#FBF5DD] rounded-xl p-3 flex gap-3" data-testid={`review-${p.id}`}>
              <button type="button" onClick={() => setPreview(p)} className="w-20 h-20 rounded-lg overflow-hidden bg-white shrink-0 border border-black/5" title="Preview" data-testid={`review-preview-${p.id}`}>
                <img src={p.image} alt={p.name} className="w-full h-full object-contain" />
              </button>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0D530E] truncate">{p.name}</p>
                    <p className="text-[11px] text-[#5C5C5C]">{fx ? `${cedis(price, fx)} · ` : ""}UK £{price}{p.weight_kg ? ` · ${p.weight_kg}kg` : ""}{p.images?.length > 1 ? ` · ${p.images.length} images` : ""}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${r.mapping_source === "memory" ? "bg-[#2D7A5D] text-white" : "bg-[#D9933B] text-white"}`}>{r.mapping_source === "memory" ? "remembered" : "AI"}</span>
                  <button type="button" onClick={() => onRemove(p)} data-testid={`review-remove-${p.id}`} className="shrink-0 text-[#B23B3B] hover:bg-[#B23B3B]/10 rounded-lg p-1.5" title="Remove (won't be added)"><Trash2 size={15} /></button>
                </div>
                {p.supplier_category && <p className="text-[11px] text-[#5C5C5C]">Supplier: “{p.supplier_category}”</p>}
                <div className="grid grid-cols-2 gap-2">
                  <select className={inputCls} value={cat} onChange={(ev) => setEdit(p.id, "category", ev.target.value)} data-testid={`review-cat-${p.id}`}>
                    {catNames.map((n) => <option key={n}>{n}</option>)}
                  </select>
                  <select className={inputCls} value={e.subcategory ?? p.subcategory} onChange={(ev) => setEdit(p.id, "subcategory", ev.target.value)} data-testid={`review-sub-${p.id}`}>
                    <option value="">— subcategory —</option>
                    {subsFor(cat).map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <input className={inputCls} placeholder="tags, comma separated" value={e.tags ?? (p.tags || []).join(", ")} onChange={(ev) => setEdit(p.id, "tags", ev.target.value)} data-testid={`review-tags-${p.id}`} />
              </div>
            </div>
          );
        })}
      </div>
      <button onClick={publish} disabled={publishing} data-testid="review-publish-btn" className="w-full bg-[#2D7A5D] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
        {publishing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Publish {imported.length} product{imported.length > 1 ? "s" : ""}
      </button>

      {preview && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="review-preview-overlay">
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
              <p className="font-display text-lg text-[#0D530E] truncate">{preview.name}</p>
              <button onClick={() => setPreview(null)} className="text-[#5C5C5C] hover:text-[#0D530E]" data-testid="review-preview-close"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              <img src={preview.image} alt="" className="w-full h-56 object-contain rounded-xl bg-[#E7E1B1]/40" />
              {preview.images?.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {preview.images.map((im, i) => <img key={i} src={im} alt="" className="w-14 h-14 rounded-lg object-cover bg-[#E7E1B1] shrink-0 border border-black/5" />)}
                </div>
              )}
              <p className="text-sm text-[#306D29] font-semibold">{fx ? `${cedis(preview.uk_price_gbp ?? preview.price_gbp, fx)} · ` : ""}UK £{preview.uk_price_gbp ?? preview.price_gbp}</p>
              {preview.description && <p className="text-sm text-[#5C5C5C]">{preview.description}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Catalog-side bulk import modal (pick ONE store, paste many URLs, review, publish) ──
export default function BulkImportModal({ stores, categories, fx, presetStore, onClose, onDone }) {
  const [store, setStore] = useState(presetStore || stores?.[0]?.name || "");
  const [urls, setUrls] = useState("");
  const [catUrl, setCatUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [removed, setRemoved] = useState(new Set());
  const [publishing, setPublishing] = useState(false);
  const [step, setStep] = useState(1);

  const imported = rows.filter((r) => r.status === "done" && !removed.has(r.product.id));

  const extractLinks = async () => {
    const cats = catUrl.split(/[\s,]+/).map((u) => u.trim()).filter((u) => u.startsWith("http"));
    if (!cats.length) { toast.error("Paste one or more category/listing page URLs"); return; }
    setExtracting(true);
    try {
      let found = [];
      for (const c of cats) {
        try { const { data } = await api.post("/admin/ai/extract-links", { url: c }); found = found.concat(data.urls || []); } catch { /* skip */ }
      }
      found = [...new Set(found)];
      if (found.length) { setUrls((u) => (u ? u + "\n" : "") + found.join("\n")); toast.success(`Found ${found.length} product${found.length > 1 ? "s" : ""}`); setCatUrl(""); }
      else toast.message("No product links found on those pages");
    } finally { setExtracting(false); }
  };

  const runImport = async () => {
    if (!store.trim()) { toast.error("Choose a store first"); return; }
    const list = urls.split("\n").map((u) => u.trim()).filter(Boolean);
    if (!list.length) { toast.error("Paste at least one product URL"); return; }
    setBusy(true);
    setRows(list.map((url) => ({ url, status: "pending" })));
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      setRows((r) => r.map((x, idx) => (idx === i ? { ...x, status: "importing" } : x)));
      try {
        const { data } = await api.post("/admin/ai/import-product", { url: list[i], store: store.trim() });
        ok += 1;
        setRows((r) => r.map((x, idx) => (idx === i ? { url: list[i], status: "done", product: data.product, mapping_source: data.mapping_source } : x)));
      } catch (e) {
        setRows((r) => r.map((x, idx) => (idx === i ? { ...x, status: "failed", error: e?.response?.data?.detail || "failed" } : x)));
      }
    }
    setBusy(false);
    if (ok) setStep(2); else toast.error("No products could be imported");
  };

  const remove = async (p) => {
    try { await api.delete(`/admin/products/${p.id}`); } catch { /* ignore */ }
    setRemoved((s) => new Set(s).add(p.id));
    toast.success("Removed — won't be added");
  };

  const publishAll = async (bodies) => {
    setPublishing(true);
    try {
      for (const b of bodies) await api.put(`/admin/products/${b.id}`, b);
      toast.success(`${bodies.length} product${bodies.length > 1 ? "s" : ""} published`);
      onDone?.(); onClose();
    } catch { toast.error("Publish failed — items stay as drafts in Catalog"); setPublishing(false); }
  };

  return (
    <Modal title="Bulk import products" onClose={onClose} testid="bulk-import-modal">
      {step === 1 && (
        <div className="space-y-3" data-testid="bulk-step-1">
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">Store (all items import under this one shop)</label>
            {stores?.length ? (
              <select className={inputCls} value={store} onChange={(e) => setStore(e.target.value)} data-testid="bulk-store">
                {stores.map((s) => <option key={s.id}>{s.name}</option>)}
              </select>
            ) : <input className={inputCls} placeholder="e.g. Boots" value={store} onChange={(e) => setStore(e.target.value)} data-testid="bulk-store" />}
          </div>
          <div className="flex gap-2">
            <textarea rows={2} className={inputCls} placeholder="Paste category/listing page URL(s) — one per line — to auto-find products" value={catUrl} onChange={(e) => setCatUrl(e.target.value)} data-testid="bulk-caturl" />
            <button onClick={extractLinks} disabled={extracting} data-testid="bulk-extract-btn" className="shrink-0 bg-[#0D530E] hover:bg-[#083b09] text-white text-sm font-semibold px-4 rounded-xl flex items-center gap-1.5 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{extracting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Find</button>
          </div>
          <textarea rows={6} className={inputCls} placeholder={"https://boots.com/product-1\nhttps://boots.com/product-2"} value={urls} onChange={(e) => setUrls(e.target.value)} data-testid="bulk-urls" />
          {rows.length > 0 && (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {r.status === "done" ? <Check size={13} className="text-[#2D7A5D]" /> : r.status === "importing" ? <Loader2 size={13} className="animate-spin text-[#306D29]" /> : r.status === "failed" ? <span className="text-[#B23B3B] font-bold">✕</span> : <span className="text-[#5C5C5C]">•</span>}
                  <span className="truncate flex-1 text-[#5C5C5C]">{r.product?.name || r.url}</span>
                  {r.status === "failed" && <span className="text-[#B23B3B] shrink-0">{r.error}</span>}
                </div>
              ))}
            </div>
          )}
          <button onClick={runImport} disabled={busy} data-testid="bulk-import-btn" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
            {busy ? <><Loader2 size={16} className="animate-spin" /> Importing…</> : <><Sparkles size={16} /> Import with AI</>}
          </button>
        </div>
      )}
      {step === 2 && (
        <ImportReview imported={imported} categories={categories} storeLabel={store} fx={fx} onRemove={remove} onPublish={publishAll} publishing={publishing} />
      )}
    </Modal>
  );
}
